/* Potential conflicts — the list above today's book.
 *
 * Everything the board can see going wrong between it and the booking system,
 * in one place and above the tabs, because none of it belongs to *Upcoming* or
 * to *In progress*: a mismatch is about a square that has already gone up, a
 * booking with nobody free for it is about one that has not, and a turn with no
 * appointment behind it is about neither.
 *
 * The store works out what the conflicts are; this file is only how they read.
 * Each item says the thing that is wrong, then who could take it instead, then
 * whatever was written on the appointment — in that order, because the note is
 * what decides whether the second line is any use.
 *
 * It draws nothing at all when there is nothing wrong. A section that is always
 * there and usually empty is a section the desk stops looking at.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const ApptConflicts = {
  name: 'ApptConflicts',
  setup: function () {
    var conflicts = store.bookConflicts;
    var clock = store.formatClock;

    // Sentences rather than text stitched around {{ }}: the compiler eats the
    // whitespace either side of an interpolation and the seams show.
    function riskWhy(x) {
      var who = x.staff ? x.staff.name : '';
      if (x.kind === 'late') {
        return x.until
          ? who + ' is in service until ' + clock(x.until) + '.'
          : who + ' is in service, with no expected finish.';
      }
      if (x.kind === 'double') {
        return who + ' is ' + (x.withRequested ? 'asked for' : 'booked') +
          ' at ' + clock(x.at) + ' as well — ' + x.withClient + ', ' +
          x.withService + '.';
      }
      if (x.kind === 'skipped') {
        return who + ' is out of the rotation — ' + x.reason.toLowerCase() + '.';
      }
      if (x.kind === 'notin') {
        return who + ' is not in until ' + clock(x.until) + '.';
      }
      if (x.kind === 'gone') {
        return who + ' is down to finish at ' + clock(x.until) +
          ', before this would be over.';
      }
      if (x.kind === 'offboard') {
        return x.labels + ' is on the booking and not on the board today.';
      }
      if (x.kind === 'nofree') {
        return x.none
          ? 'Nobody in today does ' + x.serviceName + '.'
          : 'Nobody who does ' + x.serviceName + ' is free when it runs.';
      }
      return '';
    }

    // The short label on the left, so a long list can be read down its edge.
    function riskTag(x) {
      if (x.kind === 'late') return 'May run late';
      if (x.kind === 'double') return 'Double booked';
      if (x.kind === 'skipped') return 'Skipped';
      if (x.kind === 'notin') return 'Not in yet';
      if (x.kind === 'gone') return 'Gone by then';
      if (x.kind === 'offboard') return 'Not on the board';
      return 'No tech free';
    }

    // Who could take it instead. Named, not counted: "Ann or Tam" is an
    // answer and "2 others" is another question.
    function coverLine(x) {
      var free = (x.cover || []).filter(function (c) { return c.ok; });
      if (free.length) {
        var names = free.slice(0, 3).map(function (c) {
          return c.staff.name + (store.noteWarningFor(x.clientId, c.staff.id) ? ' ⚠' : '');
        });
        var more = free.length - names.length;
        return 'Free then: ' + names.join(', ') + (more ? ' and ' + more + ' more' : '');
      }
      if (x.kind !== 'nofree' && (x.cover || []).length) {
        return 'Nobody else is free when it runs.';
      }
      return '';
    }

    // The notes behind a ⚠ in the line above: the first of the free names
    // that the client's notes mention, and what the notes say.
    function coverWarn(x) {
      var hit = (x.cover || []).filter(function (c) { return c.ok; }).slice(0, 3)
        .find(function (c) { return store.noteWarningFor(x.clientId, c.staff.id); });
      return hit ? 'Their notes mention ' + hit.staff.name + ': ' +
        store.noteWarningFor(x.clientId, hit.staff.id) : '';
    }

    function mismatchWhy(x) {
      return 'The board has ' + x.onBoard + ' on it, the ' +
        (x.onSale ? 'sale' : 'book') + ' has ' + x.inBook + '.';
    }

    /* ---- the two that are simply the wrong way round ----
     *
     * A lone mismatch has no fix here and says so: the ticket is wrong in the
     * booking system, which is not this list's to change. A pair of them does
     * have one, so the item stops pointing outward and offers it — but only
     * where the pair reads one way, which the store decides. Anything with a
     * second candidate arrives with no partner on it and reads as before.
     */

    // Which pair the pointer is on, so both halves light up together. Two
    // items about each other that highlight separately are two items.
    var lit = Vue.ref('');

    function swapWhy(x) {
      return 'The book has ' + x.clientName + ' with ' + x.swapStaff + ' and ' +
        x.swapClient + ' with ' + x.onBoard + ' — the two are the wrong way round.';
    }

    function swapWhat(x) {
      return x.swapStaff + ' is on ' + x.swapClient + ' — ' + x.swapService + '.';
    }

    return {
      conflicts: conflicts,
      open: Vue.computed(function () { return store.ui.apptRiskOpen; }),
      toggle: store.toggleApptRisk,
      formatClock: clock,
      riskWhy: riskWhy,
      riskTag: riskTag,
      coverLine: coverLine,
      coverWarn: coverWarn,
      mismatchWhy: mismatchWhy,
      lit: lit,
      swapWhy: swapWhy,
      swapWhat: swapWhat,
      askSwap: store.openSwapConfirm,
      attach: store.openAttachPick
    };
  },
  template: [
    '<section v-if="conflicts.count" class="appt-risk">',
    '  <button type="button" class="appt-risk__head" @click="toggle"',
    '    :aria-expanded="open ? \'true\' : \'false\'">',
    '    <span class="appt-risk__caret" :class="{ \'is-open\': open }">&#9656;</span>',
    '    <span class="appt-risk__title">Potential conflicts</span>',
    '    <span class="appt-risk__n">{{ conflicts.count }}</span>',
    '  </button>',
    '',
    '  <div v-if="open" class="appt-risk__body">',
    '',
    /* ---- the board and the book on different names ---- */
    '    <template v-if="conflicts.mismatch.length">',
    '      <h4 class="appt-risk__label">Assigned to somebody else in the booking system',
    '        <span>{{ conflicts.mismatch.length }}</span></h4>',
    '      <article v-for="x in conflicts.mismatch" :key="x.key"',
    '        class="risk risk--mismatch"',
    '        :class="[x.pairSide ? \'risk--pair risk--pair-\' + x.pairSide : \'\',',
    '                 { \'is-lit\': x.pairKey && lit === x.pairKey }]">',
    '        <p class="risk__top">',
    '          <span class="risk__tag">{{ x.running ? "In service" : "Done" }}</span>',
    '          <span v-if="x.pairKey" class="risk__link" aria-hidden="true">&#8646;</span>',
    '          <span class="risk__who">{{ x.clientName }}<client-note :client-id="x.clientId" /></span>',
    '          <span class="risk__what">{{ x.serviceName }}</span>',
    '        </p>',
    '        <p class="risk__why">{{ mismatchWhy(x) }}</p>',
    /* Paired, the item stops pointing at the booking system: the fix is here,
       and saying both would be saying two different things at once.
       The crossing is spelled out once, on the upper half. The lower one only
       names its counterpart — the two sit together, and the same sentence
       twice running reads as two problems rather than as one. */
    '        <template v-if="x.swapWith">',
    '          <p v-if="x.pairSide === \'a\'" class="risk__why">{{ swapWhy(x) }}</p>',
    '          <p class="risk__pair-line">{{ swapWhat(x) }}</p>',
    '          <p class="risk__act">',
    '            <button type="button" class="btn btn--primary btn--sm"',
    '              @click="askSwap(x.turnId, x.swapWith)"',
    '              @pointerenter="lit = x.pairKey" @pointerleave="lit = \'\'"',
    '              @focus="lit = x.pairKey" @blur="lit = \'\'">',
    '              Resolve &mdash; swap with {{ x.swapStaff }}</button>',
    '            <note-warn :client-id="x.clientId" :staff-id="x.swapStaffId" />',
    '            <note-warn :client-id="x.swapClientId" :staff-id="x.staffId" /></p>',
    '        </template>',
    '        <p v-else-if="x.onSale" class="risk__fix">Already paid for &mdash; move the square if the board is wrong, or fix the sale if it is. The commission follows the sale.</p>',
    '        <p v-else class="risk__fix">Fix it on the booking system &mdash; the ticket and the commission follow the book, not the board.</p>',
    '        <p v-if="x.notes" class="risk__notes">{{ x.notes }}</p>',
    '      </article>',
    '    </template>',
    '',
    /* ---- bookings that will not have the tech they need ---- */
    '    <template v-if="conflicts.risk.length">',
    '      <h4 class="appt-risk__label">Coming up without a tech ready for it',
    '        <span>{{ conflicts.risk.length }}</span></h4>',
    '      <article v-for="x in conflicts.risk" :key="x.key" class="risk"',
    '        :class="{ \'is-late\': x.late }">',
    '        <p class="risk__top">',
    '          <span class="risk__tag">{{ riskTag(x) }}</span>',
    '          <span class="risk__time">{{ formatClock(x.startsAt) }}</span>',
    '          <span class="risk__who">{{ x.clientName }}<client-note :client-id="x.clientId" /></span>',
    '          <span class="risk__letter" :class="{ \'is-unmapped\': !x.mapped }"',
    '            :style="x.color ? { background: x.color } : null">{{ x.letter }}</span>',
    '        </p>',
    '        <p class="risk__what-line">{{ x.serviceName }}',
    '          <span v-if="x.requested" class="risk__star" title="Client request">&#9733;</span>',
    '          <span v-if="x.late" class="risk__late">already due</span></p>',
    '        <p class="risk__why">{{ riskWhy(x) }}</p>',
    '        <p v-if="coverLine(x)" class="risk__cover">{{ coverLine(x) }}</p>',
    '        <p v-if="coverWarn(x)" class="risk__notes">&#9888; {{ coverWarn(x) }}</p>',
    '        <p v-if="x.notes" class="risk__notes">{{ x.notes }}</p>',
    '      </article>',
    '    </template>',
    '',
    /* ---- squares with no booking behind them ---- */
    '    <template v-if="conflicts.unlinked.length">',
    '      <h4 class="appt-risk__label">On the board with no appointment',
    '        <span>{{ conflicts.unlinked.length }}</span></h4>',
    '      <article v-for="x in conflicts.unlinked" :key="x.key" class="risk">',
    '        <p class="risk__top">',
    '          <span class="risk__tag">{{ x.running ? "Running" : "Done" }}</span>',
    '          <span class="risk__time">{{ formatClock(x.startedAt) }}</span>',
    '          <span class="risk__who">{{ x.staffName }}</span>',
    '          <span class="risk__letter" :style="x.color ? { background: x.color } : null">{{ x.letter }}</span>',
    '        </p>',
    '        <p class="risk__what-line">{{ x.serviceName }}</p>',
    '        <p class="risk__why">Nothing in the book is attached to it &mdash; a walk-in, or a booking nobody has linked.</p>',
    '        <p class="risk__act"><button type="button" class="link-btn" @click="attach(x.turnId)">Attach an appointment&hellip;</button></p>',
    '      </article>',
    '    </template>',
    '  </div>',
    '</section>'
  ].join('\n')
};
